import { ApiService } from '../../Services/Api/ApiService';

export interface BlogPost {
  id: string;
  title: string;
  content: string;
  excerpt?: string;
  imageUrl?: string;
  slug: string;
  isPublished: boolean;
  category_id: string;
  publishedAt?: string;
  createdAt?: string;
  updatedAt?: string;
  // API response'unda gelen alanlar
  created_at?: {
    date: string;
    timezone_type: number;
    timezone: string;
  };
  updated_at?: {
    date: string;
    timezone_type: number;
    timezone: string;
  };
  category?: BlogCategory;
}

export interface BlogCategory {
  id: string;
  name: string;
  slug: string;
  description?: string;
  postCount?: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface BlogPostResponse {
  status: string;
  data: {
    items: BlogPost[];
    total: number;
    page: number;
    limit: number;
  };
}

export interface BlogCategoryResponse {
  status: string;
  data: BlogCategory[];
}

export interface BlogPostDetailResponse {
  status: string;
  data: BlogPost & {
    category: BlogCategory;
  };
}

export interface BlogCategoryDetailResponse {
  status: string;
  data: BlogCategory & {
    posts: Array<{
      id: string;
      title: string;
      slug: string;
      publishedAt: string;
    }>;
  };
}

// Admin response interfaces
export interface AdminBlogPostResponse {
  status: string;
  data: BlogPost[];
}

export interface AdminBlogCategoryResponse {
  status: string;
  data: BlogCategory[];
}

export interface AdminBlogPostDetailResponse {
  status: string;
  data: BlogPost;
}

export interface AdminBlogCategoryDetailResponse {
  status: string;
  data: BlogCategory;
}

export class BlogApi {
  private static instance: BlogApi;
  private apiService: ApiService;

  private constructor() {
    this.apiService = ApiService.getInstance();
  }

  public static getInstance(): BlogApi {
    if (!BlogApi.instance) {
      BlogApi.instance = new BlogApi();
    }
    return BlogApi.instance;
  }

  // Admin endpoints (protected)
  async getAdminBlogPosts(): Promise<AdminBlogPostResponse> {
    try {
      return await this.apiService.get<AdminBlogPostResponse>('/api/v2/blog/posts');
    } catch (error) {
      console.error('❌ BlogApi: getAdminBlogPosts failed', error);
      throw error;
    }
  }

  async getAdminBlogPost(id: string): Promise<AdminBlogPostDetailResponse> {
    try {
      return await this.apiService.get<AdminBlogPostDetailResponse>(`/api/v2/blog/posts/${id}`);
    } catch (error) {
      console.error('❌ BlogApi: getAdminBlogPost failed', error);
      throw error;
    }
  }

  async createBlogPost(post: Omit<BlogPost, 'id'>): Promise<AdminBlogPostDetailResponse> {
    try {
      return await this.apiService.post<AdminBlogPostDetailResponse>('/api/v2/blog/posts', post);
    } catch (error) {
      console.error('❌ BlogApi: createBlogPost failed', error);
      throw error;
    }
  }

  async updateBlogPost(id: string, post: Partial<BlogPost>): Promise<AdminBlogPostDetailResponse> {
    try {
      return await this.apiService.put<AdminBlogPostDetailResponse>(`/api/v2/blog/posts/${id}`, post);
    } catch (error) {
      console.error('❌ BlogApi: updateBlogPost failed', error);
      throw error;
    }
  }

  async deleteBlogPost(id: string): Promise<void> {
    try {
      await this.apiService.delete<void>(`/api/v2/blog/posts/${id}`);
    } catch (error) {
      console.error('❌ BlogApi: deleteBlogPost failed', error);
      throw error;
    }
  }

  async getAdminCategories(): Promise<AdminBlogCategoryResponse> {
    try {
      return await this.apiService.get<AdminBlogCategoryResponse>('/api/v2/blog/categories');
    } catch (error) {
      console.error('❌ BlogApi: getAdminCategories failed', error);
      throw error;
    }
  }

  async getAdminCategory(id: string): Promise<AdminBlogCategoryDetailResponse> {
    try {
      return await this.apiService.get<AdminBlogCategoryDetailResponse>(`/api/v2/blog/categories/${id}`);
    } catch (error) {
      console.error('❌ BlogApi: getAdminCategory failed', error);
      throw error;
    }
  }

  async createCategory(category: Omit<BlogCategory, 'id'>): Promise<AdminBlogCategoryDetailResponse> {
    try {
      return await this.apiService.post<AdminBlogCategoryDetailResponse>('/api/v2/blog/categories', category);
    } catch (error) {
      console.error('❌ BlogApi: createCategory failed', error);
      throw error;
    }
  }

  async updateCategory(id: string, category: Partial<BlogCategory>): Promise<AdminBlogCategoryDetailResponse> {
    try {
      return await this.apiService.put<AdminBlogCategoryDetailResponse>(`/api/v2/blog/categories/${id}`, category);
    } catch (error) {
      console.error('❌ BlogApi: updateCategory failed', error);
      throw error;
    }
  }

  async deleteCategory(id: string): Promise<void> {
    try {
      await this.apiService.delete<void>(`/api/v2/blog/categories/${id}`);
    } catch (error) {
      console.error('❌ BlogApi: deleteCategory failed', error);
      throw error;
    }
  }

  // Public endpoints (unprotected)
  async getPublicBlogPosts(params?: {
    page?: number;
    limit?: number;
    category_id?: string;
    search?: string;
  }): Promise<BlogPostResponse> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.category_id) queryParams.append('category_id', params.category_id);
    if (params?.search) queryParams.append('search', params.search);

    const url = `/api/v2/public/blog/posts${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    
    try {
      return await this.apiService.get<BlogPostResponse>(url);
    } catch (error) {
      console.error('❌ BlogApi: getPublicBlogPosts failed', error);
      throw error;
    }
  }

  async getPublicBlogPost(slug: string): Promise<BlogPostDetailResponse> {
    try {
      return await this.apiService.get<BlogPostDetailResponse>(`/api/v2/public/blog/posts/${slug}`);
    } catch (error) {
      console.error('❌ BlogApi: getPublicBlogPost failed', error);
      throw error;
    }
  }

  async getPublicCategories(): Promise<BlogCategoryResponse> {
    try {
      return await this.apiService.get<BlogCategoryResponse>('/api/v2/public/blog/categories');
    } catch (error) {
      console.error('❌ BlogApi: getPublicCategories failed', error);
      throw error;
    }
  }

  async getPublicCategory(slug: string): Promise<BlogCategoryDetailResponse> {
    try {
      return await this.apiService.get<BlogCategoryDetailResponse>(`/api/v2/public/blog/categories/${slug}`);
    } catch (error) {
      console.error('❌ BlogApi: getPublicCategory failed', error);
      throw error;
    }
  }
} 